import { SVGAssets } from '@business/assets/vectors';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import {
  LayoutDashboard,
  Users,
  ChartColumn,
  FileExclamationPoint,
  Database,
  Shield,
  Menu
} from 'lucide-react';
import { Button } from '../Button';

interface AdminSidebarSubItem {
  label: string;
  path: string;
}

interface AdminSidebarItem {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  path?: string;
  children?: AdminSidebarSubItem[];
}

export const AdminSidebar = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const [openUserManagement, setOpenUserManagement] = useState<boolean>(false);
  const [openCustomerAnalytics, setOpenCustomerAnalytics] =
    useState<boolean>(false);
  const [openContentModeration, setOpenContentModeration] =
    useState<boolean>(false);
  const [openSubscriptionMgmt, setOpenSubscriptionMgmt] =
    useState<boolean>(false);
  const [openAnalytics, setOpenAnalytics] = useState<boolean>(false);
  const [openSystemAdmin, setOpenSystemAdmin] = useState<boolean>(false);
  const [openSecurity, setOpenSecurity] = useState<boolean>(false);
  const [openFinancial, setOpenFinancial] = useState<boolean>(false);

  useEffect(() => {
    const p = location.pathname;
    if (p.startsWith('/admin/user-management')) setOpenUserManagement(true);
    else if (p.startsWith('/admin/customer-analytics'))
      setOpenCustomerAnalytics(true);
    else if (p.startsWith('/admin/content-moderation'))
      setOpenContentModeration(true);
    else if (p.startsWith('/admin/subscription-management'))
      setOpenSubscriptionMgmt(true);
    else if (p.startsWith('/admin/analytics')) setOpenAnalytics(true);
    else if (p.startsWith('/admin/system-admin')) setOpenSystemAdmin(true);
    else if (p.startsWith('/admin/security')) setOpenSecurity(true);
    else if (p.startsWith('/admin/financial')) setOpenFinancial(true);
  }, []);

  const sidebarItems: AdminSidebarItem[] = [
    {
      icon: LayoutDashboard,
      label: t('admin.dashboard'),
      path: '/admin'
    },
    {
      icon: Users,
      label: t('admin.userManagement'),
      children: [
        {
          label: t('admin.userDirectory'),
          path: '/admin/user-management/user-directory'
        },
        {
          label: t('admin.userSegmentation'),
          path: '/admin/user-management/user-segmentation'
        }
      ]
    },
    {
      icon: ChartColumn,
      label: t('admin.customerAnalytics'),
      children: [
        {
          label: t('admin.customerReports'),
          path: '/admin/customer-analytics/customer-reports'
        },
        {
          label: t('admin.customerCohortAnalysisMenu'),
          path: '/admin/customer-analytics/cohort-analysis'
        },
        {
          label: t('admin.customerLifetimeValueMenu'),
          path: '/admin/customer-analytics/lifetime-value'
        },
        {
          label: t('admin.customerEngagementMetricsMenu'),
          path: '/admin/customer-analytics/engagement-metrics'
        }
      ]
    },
    {
      icon: FileExclamationPoint,
      label: t('admin.contentModeration'),
      children: [
        {
          label: t('admin.aiModeration'),
          path: '/admin/content-moderation/ai-moderation'
        },
        {
          label: t('admin.accountLevelAlerts'),
          path: '/admin/content-moderation/account-level-alerts'
        }
      ]
    },
    {
      icon: SVGAssets.CreditCardIcon,
      label: t('admin.subscriptionMgmt'),
      children: [
        {
          label: t('admin.subscriptionDashboard'),
          path: '/admin/subscription-management/subscription-dashboard'
        },
        {
          label: t('admin.subscriptionList'),
          path: '/admin/subscription-management/subscription-list'
        },
        {
          label: t('admin.tierManagement'),
          path: '/admin/subscription-management/tier-management'
        },
        {
          label: t('admin.analytics'),
          path: '/admin/subscription-management/analytics'
        }
      ]
    },
    {
      icon: ChartColumn,
      label: t('admin.analytics'),
      children: [
        {
          label: t('admin.overview'),
          path: '/admin/analytics/overview'
        },
        {
          label: t('admin.salesRevenue'),
          path: '/admin/analytics/sales-revenue'
        },
        {
          label: t('admin.productPerformance'),
          path: '/admin/analytics/product-performance'
        }
      ]
    },
    {
      icon: Database,
      label: t('admin.financial'),
      children: [
        {
          label: t('admin.financialAnalytics'),
          path: '/admin/financial/analytics'
        },
        {
          label: t('admin.paymentsAndBilling'),
          path: '/admin/financial/payments-billing'
        }
      ]
    },
    {
      icon: SVGAssets.SettingsIcon,
      label: t('admin.systemAdmin'),
      children: [
        {
          label: t('admin.adminUsers'),
          path: '/admin/system-admin/admin-users'
        },
        {
          label: t('admin.systemSettings'),
          path: '/admin/system-admin/system-settings'
        },
        {
          label: t('admin.integrations'),
          path: '/admin/system-admin/integrations'
        }
      ]
    },
    {
      icon: Shield,
      label: t('admin.security'),
      children: [
        {
          label: t('admin.storageManagement'),
          path: '/admin/security/storage-management'
        }
      ]
    }
  ];

  const isParentActive = (item: AdminSidebarItem) => {
    if (item.path && location.pathname === item.path) {
      return true;
    }
    if (item.children) {
      return item.children.some(
        child =>
          location.pathname === child.path ||
          location.pathname.startsWith(child.path + '/')
      );
    }
    return false;
  };

  const handleItemClick = (item: AdminSidebarItem) => {
    if (isCollapsed) {
      setIsCollapsed(false);
    }
    if (item.children) {
      // Toggle submenu
      if (item.label === t('admin.userManagement')) {
        setOpenUserManagement(!openUserManagement);
      } else if (item.label === t('admin.customerAnalytics')) {
        setOpenCustomerAnalytics(!openCustomerAnalytics);
      } else if (item.label === t('admin.contentModeration')) {
        setOpenContentModeration(!openContentModeration);
      } else if (item.label === t('admin.subscriptionMgmt')) {
        setOpenSubscriptionMgmt(!openSubscriptionMgmt);
      } else if (item.label === t('admin.analytics')) {
        setOpenAnalytics(!openAnalytics);
      } else if (item.label === t('admin.systemAdmin')) {
        setOpenSystemAdmin(!openSystemAdmin);
      } else if (item.label === t('admin.security')) {
        setOpenSecurity(!openSecurity);
      } else if (item.label === t('admin.financial')) {
        setOpenFinancial(!openFinancial);
      }
    } else if (item.path) {
      navigate(item.path);
    }
  };

  return (
    <aside
      className={`bg-white border-r border-gray-200 flex-shrink-0 overflow-y-auto transition-all duration-300 ${
        isCollapsed ? 'w-16' : 'w-64'
      }`}>
      {/* Header with toggle */}
      <div
        className={`px-4 py-6 border-b border-gray-200 flex flex-row ${isCollapsed ? 'justify-center' : 'justify-between'} items-center w-full`}>
        {!isCollapsed && (
          <span className="text-sidebar-foreground font-bold text-base tracking-tight truncate">
            Admin Console
          </span>
        )}
        <Button
          btnStyle="link"
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="w-fit flex items-center justify-start text-gray-700 hover:text-gray-900"
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
          {isCollapsed ? (
            <Menu className="w-5 h-5" />
          ) : (
            <SVGAssets.CloseIconGray className="w-3 h-3" />
          )}
          {/* {!isCollapsed && (
            <span className="ml-4 font-semibold text-lg">Admin Console</span>
          )} */}
        </Button>
      </div>

      {/* Navigation */}
      <nav className="py-3 px-2 space-y-0.5">
        {sidebarItems.map((item, index) => {
          const active = isParentActive(item);
          const Icon = item.icon;
          const hasChildren = item.children && item.children.length > 0;
          const isUserMgmtOpen =
            item.label === t('admin.userManagement') && openUserManagement;
          const isCustomerAnalyticsOpen =
            item.label === t('admin.customerAnalytics') &&
            openCustomerAnalytics;
          const isContentModerationOpen =
            item.label === t('admin.contentModeration') &&
            openContentModeration;
          const isSubMgmtOpen =
            item.label === t('admin.subscriptionMgmt') && openSubscriptionMgmt;
          const isAnalyticsOpen =
            item.label === t('admin.analytics') && openAnalytics;
          const isSystemAdminOpen =
            item.label === t('admin.systemAdmin') && openSystemAdmin;
          const isSecurityOpen =
            item.label === t('admin.security') && openSecurity;
          const isFinancialOpen =
            item.label === t('admin.financial') && openFinancial;
          const isOpen =
            isUserMgmtOpen ||
            isCustomerAnalyticsOpen ||
            isContentModerationOpen ||
            isSubMgmtOpen ||
            isAnalyticsOpen ||
            isSystemAdminOpen ||
            isSecurityOpen ||
            isFinancialOpen;

          return (
            <div key={index}>
              {/* MAIN MENU */}
              <Button
                btnStyle="link"
                onClick={() => handleItemClick(item)}
                className={`
                  w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left transition-colors
                  ${
                    active
                      ? 'bg-[#49712B] text-white font-medium'
                      : 'text-[#505050] hover:bg-[#ebe5e5]'
                  }
                  ${isCollapsed ? 'justify-center' : ''}
                `}
                title={isCollapsed ? item.label : undefined}>
                <Icon className="w-4 h-4 flex-shrink-0" />
                {!isCollapsed && (
                  <>
                    <span className="text-sm font-semibold flex-1">
                      {item.label}
                    </span>
                    {hasChildren && (
                      <SVGAssets.ChevronDown
                        className={`w-4 h-4 transition-transform ${active ? 'text-white' : 'text-gray-400'} ${
                          isOpen ? 'rotate-180' : ''
                        }`}
                      />
                    )}
                  </>
                )}
              </Button>

              {/* SUBMENU */}
              {!isCollapsed && hasChildren && isOpen && (
                <div className="ml-4 mt-0.5 space-y-0.5 transition-all duration-300 border-l border-sidebar-border">
                  {item.children!.map((sub, idx) => {
                    const subActive = location.pathname === sub.path;

                    return (
                      <button
                        onClick={() => navigate(sub.path)}
                        key={idx}
                        className={`
                          w-full text-left
                          flex items-center gap-3 px-4 py-2 text-xs ml-2 rounded-lg
                          text-[#505050] font-semibold
                          ${subActive ? 'bg-[#ebe5e5]' : 'hover:bg-[#ebe5e5]'}
                        `}>
                        <span>{sub.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </aside>
  );
};
